import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { HeldRole, PermissionDefinition, RoleDefinition } from '../lib/types';
import { RoleBadge } from '../components/roles/RoleBadge';
import { Button, EmptyState, ErrorState, Panel } from '../components/ui';
import { timeAgo } from '../lib/format';

/* =========================================================================
   Two pages in one file, because they are two views of the same data.
   ========================================================================= */

/**
 * The public team page.
 *
 * Readable by anyone on purpose. The whole reason a Founder ribbon exists is
 * that other players see it, so putting the list of who holds one behind a
 * permission would defeat the feature it is meant to serve.
 */
interface TeamResponse {
  roles: Array<
    HeldRole & {
      description: string;
      members: Array<{ username: string; trainerName: string; title: string; since: string }>;
    }
  >;
}

export function TeamPage() {
  const state = useAsync<TeamResponse>(() => api.get('/roles/team'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const roles = (state.data?.roles ?? []).filter((role) => role.members.length > 0);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">The Team</h1>
          <div className="page-head__sub">Who builds, draws and looks after PokéGrove.</div>
        </div>
      </div>

      {state.loading ? (
        <div className="pxwell skeleton" style={{ height: 220 }} />
      ) : roles.length === 0 ? (
        <EmptyState art="◈" title="Nobody listed yet">
          Roles have not been handed out.
        </EmptyState>
      ) : (
        roles.map((role) => (
          <Panel key={role.key} title={role.name} hint={role.description}>
            <div className="team-role">
              <RoleBadge role={role} size={56} />
              <div className="team-role__members">
                {role.members.map((member) => (
                  <Link
                    key={member.username}
                    to={`/trainer/${member.username}`}
                    className="pxwell team-member"
                  >
                    <strong>{member.trainerName}</strong>
                    <span className="muted">@{member.username}</span>
                  </Link>
                ))}
              </div>
            </div>
          </Panel>
        ))
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Administration
   ------------------------------------------------------------------------- */

interface CatalogResponse {
  roles: RoleDefinition[];
  permissions: PermissionDefinition[];
}

interface UserRolesResponse {
  user: {
    id: string;
    username: string;
    trainerName: string;
    title: string;
    level: number;
    isBanned: boolean;
    isNpc: boolean;
    accountStatus: string;
    joinedAt: string;
    lastSeenAt: string;
  };
  roles: Array<{
    key: string;
    name: string;
    tag: string;
    grantedAt: string;
    grantedBy: string | null;
    note: string | null;
  }>;
  permissions: string[];
}

/**
 * The role editor.
 *
 * A search box, a set of checkboxes, and a preview of what the change adds up
 * to *before* it is saved. The preview matters: the whole failure mode this
 * system replaces is somebody being handed powers nobody intended, and the
 * cheapest guard against that is showing the resulting permission list next to
 * the Save button.
 */
export function RoleAdminPage() {
  const { me } = useSession();
  const { push } = useToast();
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const catalog = useAsync<CatalogResponse>(() => api.get('/roles'));
  const detail = useAsync<UserRolesResponse | null>(
    () => (target ? api.get(`/roles/user/${target}`) : Promise.resolve(null)),
    [target],
  );

  const canEdit = me?.permissions.includes('roles.manage') ?? false;

  if (catalog.error) return <ErrorState message={catalog.error} onRetry={catalog.reload} />;

  const roles = catalog.data?.roles ?? [];
  const permissions = catalog.data?.permissions ?? [];

  // Null means "not touched yet", so the checkboxes show what the server said
  // until the administrator actually changes something.
  const chosen = picked ?? detail.data?.roles.map((role) => role.key) ?? [];

  // What the *pending* selection would grant — computed from the catalog, the
  // same union the server will compute, so the preview cannot lie.
  const previewPermissions = new Set(
    roles.filter((role) => chosen.includes(role.key)).flatMap((role) => role.permissions),
  );

  const dirty =
    detail.data !== null &&
    picked !== null &&
    JSON.stringify([...chosen].sort()) !==
      JSON.stringify(detail.data.roles.map((role) => role.key).sort());

  async function save() {
    if (!detail.data) return;
    setSaving(true);
    try {
      await api.put(`/roles/user/${detail.data.user.username}`, {
        roleKeys: chosen,
        reason: reason.trim() || undefined,
      });
      push('success', `Roles updated for ${detail.data.user.username}.`);
      setPicked(null);
      setReason('');
      detail.reload();
      catalog.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save roles.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Roles &amp; Permissions</h1>
          <div className="page-head__sub">
            {roles.length} roles · {permissions.length} permissions
          </div>
        </div>
        <Link to="/admin/audit" className="btn btn--sm">
          Audit log
        </Link>
      </div>

      <Panel title="◈ Find a trainer">
        <form
          className="row row--wrap"
          onSubmit={(event) => {
            event.preventDefault();
            setPicked(null);
            setTarget(search.trim() || null);
          }}
        >
          <input
            className="input"
            style={{ flex: 1, minWidth: 200 }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Username"
            aria-label="Username"
          />
          <Button type="submit" variant="info">
            Look up
          </Button>
        </form>
      </Panel>

      {detail.error && <ErrorState message={detail.error} onRetry={detail.reload} />}

      {detail.data && (
        <>
          <Panel
            title={`◆ ${detail.data.user.trainerName}`}
            hint={`@${detail.data.user.username} · Lv ${detail.data.user.level}`}
          >
            <div className="row row--wrap tiny muted" style={{ gap: 'var(--gap)' }}>
              <span>{detail.data.user.accountStatus}</span>
              <span>Joined {timeAgo(detail.data.user.joinedAt)}</span>
              <span>Seen {timeAgo(detail.data.user.lastSeenAt)}</span>
              {detail.data.user.isBanned && <span style={{ color: 'var(--berry)' }}>SUSPENDED</span>}
            </div>

            {detail.data.roles.length > 0 && (
              <div className="stack-sm" style={{ marginTop: 'var(--gap-sm)' }}>
                {detail.data.roles.map((role) => (
                  <div key={role.key} className="tiny muted">
                    <strong>{role.name}</strong> — granted{' '}
                    {role.grantedBy ? `by ${role.grantedBy}` : 'by the system'} {timeAgo(role.grantedAt)}
                    {role.note && ` · ${role.note}`}
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="☑ Roles" hint={canEdit ? undefined : 'Read only — you cannot manage roles'}>
            <div className="role-editor">
              {roles.map((role) => (
                <label
                  key={role.key}
                  className="role-option"
                  style={{ ['--role-accent' as string]: role.accent }}
                >
                  <input
                    type="checkbox"
                    checked={chosen.includes(role.key)}
                    disabled={!canEdit || detail.data!.user.isNpc}
                    onChange={(event) =>
                      setPicked(
                        event.target.checked
                          ? [...chosen, role.key]
                          : chosen.filter((key) => key !== role.key),
                      )
                    }
                  />
                  <RoleBadge role={role} size={26} />
                  <div className="role-option__body">
                    <div className="role-option__name">{role.name}</div>
                    <div className="role-option__desc">
                      {role.permissions.length === 0
                        ? 'Recognition only — grants nothing.'
                        : `${role.permissions.length} permission(s)`}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="stack-sm" style={{ marginTop: 'var(--gap)' }}>
              <div className="tiny muted">
                {dirty ? 'This would grant:' : 'Currently grants:'}
              </div>
              <div className="permission-grid">
                {permissions.map((permission) => (
                  <span
                    key={permission.key}
                    className={`permission-pill ${
                      previewPermissions.has(permission.key) ? 'permission-pill--on' : ''
                    }`}
                    title={permission.description}
                  >
                    {permission.name}
                  </span>
                ))}
              </div>
            </div>

            {canEdit && (
              <div className="stack-sm" style={{ marginTop: 'var(--gap)' }}>
                <div className="field">
                  <label className="field__label" htmlFor="role-reason">
                    Reason <span className="muted">(optional, written to the audit log)</span>
                  </label>
                  <input
                    id="role-reason"
                    className="input"
                    value={reason}
                    maxLength={280}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Joining the moderation team"
                  />
                </div>
                <div className="row row--between">
                  <button
                    type="button"
                    className="linkbtn tiny"
                    onClick={() => {
                      setPicked(null);
                      setReason('');
                    }}
                    disabled={!dirty}
                  >
                    Reset
                  </button>
                  <Button variant="go" onClick={() => void save()} loading={saving} disabled={!dirty}>
                    Save roles
                  </Button>
                </div>
              </div>
            )}
          </Panel>
        </>
      )}

      <Panel title="▤ Every role">
        <div className="stack-sm">
          {roles.map((role) => (
            <div key={role.key} className="pxwell row" style={{ padding: 'var(--gap-sm)', gap: 'var(--gap-sm)' }}>
              <RoleBadge role={role} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono tiny">
                  {role.name} <span className="muted">· {role.holders} holder(s)</span>
                </div>
                <div className="tiny muted">{role.description}</div>
              </div>
              <span className="tiny mono muted">{role.permissions.length || '—'}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Audit
   ------------------------------------------------------------------------- */

interface AuditResponse {
  entries: Array<{
    id: string;
    action: string;
    actor: string;
    target: string | null;
    before: string[] | null;
    after: string[] | null;
    reason: string | null;
    at: string;
  }>;
}

/** Every change to what somebody may do, newest first. */
export function AuditPage() {
  const [username, setUsername] = useState('');
  const [filter, setFilter] = useState('');
  const state = useAsync<AuditResponse>(
    () => api.get('/roles/audit', { username: filter || undefined }),
    [filter],
  );

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const entries = state.data?.entries ?? [];
  const list = (value: string[] | null) => (value && value.length > 0 ? value.join(', ') : 'none');

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Audit log</h1>
          <div className="page-head__sub">Every change to roles and permissions.</div>
        </div>
        <Link to="/admin/roles" className="btn btn--sm">
          Roles
        </Link>
      </div>

      <Panel raised={false}>
        <form
          className="row row--wrap"
          onSubmit={(event) => {
            event.preventDefault();
            setFilter(username.trim());
          }}
        >
          <input
            className="input"
            style={{ flex: 1, minWidth: 200 }}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="Filter by username"
            aria-label="Filter by username"
          />
          <Button type="submit">Filter</Button>
        </form>
      </Panel>

      <Panel title={`◷ ${entries.length} entries`}>
        {state.loading ? (
          <div className="pxwell skeleton" style={{ height: 200 }} />
        ) : entries.length === 0 ? (
          <EmptyState art="◌" title="Nothing logged">
            No role changes have been made.
          </EmptyState>
        ) : (
          <div className="stack-sm">
            {entries.map((entry) => (
              <div key={entry.id} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
                <div className="row row--between">
                  <span className="mono tiny">
                    {entry.actor} → {entry.target ?? '—'}
                  </span>
                  <span className="tiny muted">{timeAgo(entry.at)}</span>
                </div>
                <div className="tiny muted" style={{ marginTop: 2 }}>
                  <span className="chip chip--sm">{entry.action}</span>{' '}
                  {list(entry.before)} → <strong>{list(entry.after)}</strong>
                </div>
                {entry.reason && <div className="tiny" style={{ marginTop: 2 }}>“{entry.reason}”</div>}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
